import { createContext } from 'preact';
import { useContext, useReducer } from 'preact/hooks';
import { tableSelectionKey } from './lib/tableMapUtils';
import { isWidgetHybridBooking } from './lib/widgetBookingPath';
import { getEventOccurrenceEntries } from './lib/eventOccurrenceDates';
import type {
    WidgetConfig,
    EventData,
    FeeStructure,
    SponsorData,
    VenueData,
    VenueConfig,
    SeatStatusSection,
    SeatAvailabilityMap,
    TableAvailabilityMap,
    PricingRule,
    TablePricingRule,
    CartItem,
    AppliedDiscount,
    ContactInfo,
    WidgetStep,
    WidgetBookingPath,
    PurchaseResult,
    SelectedSeat,
    SelectedTable,
    FeeCalculationResponse,
} from './types';

export interface WidgetState {
    step: WidgetStep;
    widgetId: string;
    /** Referral code from ?ref= in URL — included in sale metadata */
    refCode: string;
    widget: WidgetConfig | null;
    event: EventData | null;
    feeStructures: FeeStructure[];
    sponsors: SponsorData[];
    venue: VenueData | null;
    venueConfig: VenueConfig | null;
    seatStatus: Record<string, SeatStatusSection> | null;
    seatAvailability: SeatAvailabilityMap | null;
    tableAvailability: TableAvailabilityMap | null;
    seatPricing: PricingRule[];
    tablePricing: TablePricingRule[];
    selectedSeats: SelectedSeat[];
    selectedTables: SelectedTable[];
    holdId: string | null;
    holdExpiresAt: number | null;
    /** Which API created `holdId` — release uses widget hold vs public events-tables hold. */
    holdApiSource: 'widget' | 'event_tables' | null;
    /** After `POST …/events-tables/…/hold` — line total in **major** units (overrides sum of table `pricingRule` in Payment). */
    tableReservationHoldCents: number | null;
    stripePublishableKey: string;
    paymentRail: import('./lib/paymentRail').PaymentRail | null;
    cart: CartItem[];
    discount: AppliedDiscount | null;
    unlockedSponsorTiers: Record<string, { value: number; type: string; code: string }>;
    contact: ContactInfo;
    purchaseResult: PurchaseResult | null;
    errorMessage: string | null;
    sponsorVideoTierId: string | null;
    feeCalculation: FeeCalculationResponse | null;
    calculatingFees: boolean;
    /** Override from URL/data: when true, enable post-purchase registration modal */
    supportRegistrationOverride?: boolean;
    /** User's referral code from post-registration verify response — used for gift referral block */
    userReferralCode: string;
    /** UUID: POST /views + sale metadata (set when sponsor video completes) */
    viewCorrelationId: string | undefined;
    /** AD-vantage: event_sponsor ids when user completed sponsor video (paid flows) */
    watchedSponsorAttributions: Array<{ event_sponsor_id: string }>;
    /** Sequential sponsor lead prompts after discount unlock */
    sponsorLeadQueue: Array<{ eventSponsorId: string; sponsorName: string }>;
    /** Last lead consent — attached to purchase metadata */
    sponsorLeadConsent?: { event_sponsor_id: string; interested: boolean };
    /** Multi-date selection (ISO). Initialized to the earliest still-purchasable occurrence on load; see `eventOccurrenceDates` helpers. */
    selectedOccurrenceIso: string | null;
    /**
     * Captured on the first `SET_DATA` only — used to refetch the bootstrap event when the user returns from a
     * linked child occurrence to the “home” day (`widgetBootstrapFirstOccurrenceIso`).
     */
    widgetBootstrapEventId: string | null;
    /** First occurrence ISO from the bootstrap event (stable across linked navigations). */
    widgetBootstrapFirstOccurrenceIso: string | null;
    /**
     * When the widget offers both table reservation and ticket tiers, this selects which
     * flow to show. Ignored for single-path events.
     */
    widgetBookingPath: WidgetBookingPath;
}

export type WidgetAction =
    | {
          type: 'SET_DATA';
          payload: NonNullable<WidgetState['widget']> extends never
              ? never
              : {
                    widget: WidgetConfig;
                    event: EventData;
                    feeStructures: FeeStructure[];
                    sponsors: SponsorData[];
                    venue: VenueData | null;
                    venueConfig: VenueConfig | null;
                    seatStatus: Record<string, SeatStatusSection> | null;
                    stripePublishableKey: string;
                    paymentRail?: import('./lib/paymentRail').PaymentRail | null;
                    /**
                     * After loading a linked event (multi-date), keep the user’s chosen date if it
                     * is still a purchasable row on the new event. Otherwise derived from the list.
                     */
                    selectedOccurrenceIsoOverride?: string | null;
                };
      }
    | { type: 'SET_STEP'; step: WidgetStep }
    | { type: 'SET_CART'; cart: CartItem[] }
    | { type: 'UPDATE_QUANTITY'; tierId: string; quantity: number }
    | { type: 'SET_DISCOUNT'; discount: AppliedDiscount | null }
    | { type: 'UNLOCK_SPONSOR_TIER'; tierId: string; value: number; discountType: string; code: string }
    | { type: 'SET_CONTACT'; contact: ContactInfo }
    | { type: 'SET_PURCHASE_RESULT'; result: PurchaseResult }
    | { type: 'SET_ERROR'; message: string }
    | { type: 'SET_SPONSOR_VIDEO_TIER'; tierId: string | null }
    | {
          type: 'SET_SEAT_MAP';
          seatAvailability: SeatAvailabilityMap;
          tableAvailability: TableAvailabilityMap;
          seatPricing: PricingRule[];
          tablePricing: TablePricingRule[];
      }
    | { type: 'TOGGLE_SEAT'; seat: SelectedSeat }
    | { type: 'TOGGLE_TABLE'; table: SelectedTable }
    | { type: 'REPLACE_TABLE_SELECTION'; tables: SelectedTable[] }
    | { type: 'CLEAR_SELECTION' }
    | {
          type: 'SET_HOLD';
          holdId: string;
          expiresIn: number;
          holdPricingCents?: number | null;
          holdApiSource?: 'widget' | 'event_tables';
      }
    | { type: 'CLEAR_HOLD' }
    | { type: 'SET_CALCULATING_FEES'; calculating: boolean }
    | { type: 'SET_FEE_CALCULATION'; feeCalculation: FeeCalculationResponse }
    | { type: 'CLEAR_FEE_CALCULATION' }
    | { type: 'SET_SUPPORT_REGISTRATION'; enabled: boolean }
    | { type: 'SET_USER_REFERRAL_CODE'; code: string }
    | { type: 'SET_VIEW_CORRELATION_ID'; correlationId: string | undefined }
    | { type: 'ADD_WATCHED_SPONSOR_ATTRIBUTION'; event_sponsor_id: string }
    | { type: 'START_SPONSOR_LEAD_QUEUE'; prompts: Array<{ eventSponsorId: string; sponsorName: string }> }
    | { type: 'POP_SPONSOR_LEAD_QUEUE' }
    | { type: 'SET_SPONSOR_LEAD_CONSENT'; event_sponsor_id: string; interested: boolean }
    | { type: 'SET_SELECTED_OCCURRENCE_ISO'; iso: string | null }
    /** After a successful table (or other) flow — back to shopping without reloading */
    | { type: 'RESET_WIDGET_PURCHASE' }
    | { type: 'SET_WIDGET_BOOKING_PATH'; path: WidgetBookingPath };

export const initialState: WidgetState = {
    step: 'loading',
    widgetId: '',
    refCode: '',
    widget: null,
    event: null,
    feeStructures: [],
    sponsors: [],
    venue: null,
    venueConfig: null,
    seatStatus: null,
    seatAvailability: null,
    tableAvailability: null,
    seatPricing: [],
    tablePricing: [],
    selectedSeats: [],
    selectedTables: [],
    holdId: null,
    holdExpiresAt: null,
    holdApiSource: null,
    tableReservationHoldCents: null,
    stripePublishableKey: '',
    paymentRail: null,
    cart: [],
    discount: null,
    unlockedSponsorTiers: {},
    contact: { name: '', email: '', phone: '' },
    purchaseResult: null,
    errorMessage: null,
    sponsorVideoTierId: null,
    feeCalculation: null,
    calculatingFees: false,
    supportRegistrationOverride: true,
    userReferralCode: '',
    viewCorrelationId: undefined,
    watchedSponsorAttributions: [],
    sponsorLeadQueue: [],
    sponsorLeadConsent: undefined,
    selectedOccurrenceIso: null,
    widgetBootstrapEventId: null,
    widgetBootstrapFirstOccurrenceIso: null,
    widgetBookingPath: 'tickets',
};

export function widgetReducer(state: WidgetState, action: WidgetAction): WidgetState {
    switch (action.type) {
        case 'SET_DATA': {
            const hybrid = isWidgetHybridBooking(
                action.payload.event,
                action.payload.venue,
                action.payload.feeStructures,
            );
            const bootstrapId = state.widgetBootstrapEventId ?? action.payload.event?._id ?? null;
            const bootstrapFirstIso =
                state.widgetBootstrapFirstOccurrenceIso ??
                getEventOccurrenceEntries(action.payload.event as unknown)[0]?.iso ??
                null;
            return {
                ...state,
                step: 'tickets',
                widget: action.payload.widget,
                event: action.payload.event,
                feeStructures: action.payload.feeStructures,
                sponsors: action.payload.sponsors,
                venue: action.payload.venue,
                venueConfig: action.payload.venueConfig,
                seatStatus: action.payload.seatStatus,
                stripePublishableKey: action.payload.stripePublishableKey,
                paymentRail: action.payload.paymentRail ?? null,
                viewCorrelationId: undefined,
                watchedSponsorAttributions: [],
                sponsorLeadQueue: [],
                sponsorLeadConsent: undefined,
                holdId: null,
                holdExpiresAt: null,
                holdApiSource: null,
                tableReservationHoldCents: null,
                selectedSeats: [],
                selectedTables: [],
                selectedOccurrenceIso: null,
                widgetBootstrapEventId: bootstrapId,
                widgetBootstrapFirstOccurrenceIso: bootstrapFirstIso,
                /** Hybrid (tickets + tables): default to table flow first */
                widgetBookingPath: hybrid ? 'tables' : 'tickets',
            };
        }
        case 'SET_SELECTED_OCCURRENCE_ISO':
            return { ...state, selectedOccurrenceIso: action.iso };
        case 'SET_STEP':
            return { ...state, step: action.step };
        case 'SET_CART':
            return { ...state, cart: action.cart };
        case 'UPDATE_QUANTITY': {
            const existing = state.cart.find(c => c.tier._id === action.tierId);
            if (action.quantity <= 0) {
                return {
                    ...state,
                    cart: state.cart.filter(c => c.tier._id !== action.tierId),
                };
            }
            if (existing) {
                return {
                    ...state,
                    cart: state.cart.map(c => (c.tier._id === action.tierId ? { ...c, quantity: action.quantity } : c)),
                };
            }
            const tier = state.feeStructures.find(f => f._id === action.tierId);
            if (!tier) return state;
            return {
                ...state,
                cart: [...state.cart, { tier, quantity: action.quantity }],
            };
        }
        case 'SET_DISCOUNT':
            return { ...state, discount: action.discount };
        case 'UNLOCK_SPONSOR_TIER':
            return {
                ...state,
                unlockedSponsorTiers: {
                    ...state.unlockedSponsorTiers,
                    [action.tierId]: { value: action.value, type: action.discountType, code: action.code },
                },
            };
        case 'SET_CONTACT':
            return { ...state, contact: action.contact };
        case 'SET_PURCHASE_RESULT':
            return {
                ...state,
                step: 'confirmation',
                purchaseResult: action.result,
                viewCorrelationId: undefined,
                watchedSponsorAttributions: [],
                sponsorLeadQueue: [],
                sponsorLeadConsent: undefined,
            };
        case 'SET_ERROR':
            return { ...state, step: 'error', errorMessage: action.message };
        case 'SET_SPONSOR_VIDEO_TIER':
            return { ...state, sponsorVideoTierId: action.tierId };
        case 'SET_SEAT_MAP':
            return {
                ...state,
                seatAvailability: action.seatAvailability,
                tableAvailability: action.tableAvailability,
                seatPricing: action.seatPricing,
                tablePricing: action.tablePricing,
            };
        case 'TOGGLE_SEAT': {
            const key = `${action.seat.sectionId}-${action.seat.row}-${action.seat.seat}`;
            const exists = state.selectedSeats.some(s => `${s.sectionId}-${s.row}-${s.seat}` === key);
            return {
                ...state,
                selectedSeats: exists
                    ? state.selectedSeats.filter(s => `${s.sectionId}-${s.row}-${s.seat}` !== key)
                    : [...state.selectedSeats, action.seat],
            };
        }
        case 'REPLACE_TABLE_SELECTION':
            return { ...state, selectedSeats: [], selectedTables: action.tables };
        case 'TOGGLE_TABLE': {
            const next = {
                ...action.table,
                chairs: action.table.chairs ?? 1,
            };
            const tKey = tableSelectionKey(next.tableId, next.timeSlot);
            const tExists = state.selectedTables.some(t => {
                const k = tableSelectionKey(t.tableId, t.timeSlot);
                return k === tKey;
            });
            return {
                ...state,
                selectedTables: tExists
                    ? state.selectedTables.filter(t => {
                          const k = tableSelectionKey(t.tableId, t.timeSlot);
                          return k !== tKey;
                      })
                    : [...state.selectedTables, next],
            };
        }
        case 'CLEAR_SELECTION':
            return { ...state, selectedSeats: [], selectedTables: [] };
        case 'SET_HOLD': {
            const nextSource = action.holdApiSource ?? 'widget';
            return {
                ...state,
                holdId: action.holdId,
                holdExpiresAt: Date.now() + action.expiresIn * 1000,
                holdApiSource: nextSource,
                tableReservationHoldCents: nextSource === 'event_tables' ? (action.holdPricingCents ?? null) : null,
            };
        }
        case 'CLEAR_HOLD':
            return {
                ...state,
                holdId: null,
                holdExpiresAt: null,
                holdApiSource: null,
                tableReservationHoldCents: null,
            };
        case 'SET_CALCULATING_FEES':
            return { ...state, calculatingFees: action.calculating };
        case 'SET_FEE_CALCULATION':
            return { ...state, feeCalculation: action.feeCalculation, calculatingFees: false };
        case 'CLEAR_FEE_CALCULATION':
            return { ...state, feeCalculation: null, calculatingFees: false };
        case 'SET_SUPPORT_REGISTRATION':
            return { ...state, supportRegistrationOverride: action.enabled };
        case 'SET_USER_REFERRAL_CODE':
            return { ...state, userReferralCode: action.code };
        case 'SET_VIEW_CORRELATION_ID':
            return { ...state, viewCorrelationId: action.correlationId };
        case 'ADD_WATCHED_SPONSOR_ATTRIBUTION': {
            const list = state.watchedSponsorAttributions;
            if (list.some(a => a.event_sponsor_id === action.event_sponsor_id)) return state;
            return {
                ...state,
                watchedSponsorAttributions: [...list, { event_sponsor_id: action.event_sponsor_id }],
            };
        }
        case 'START_SPONSOR_LEAD_QUEUE':
            return { ...state, sponsorLeadQueue: action.prompts };
        case 'POP_SPONSOR_LEAD_QUEUE':
            return { ...state, sponsorLeadQueue: state.sponsorLeadQueue.slice(1) };
        case 'SET_SPONSOR_LEAD_CONSENT':
            return {
                ...state,
                sponsorLeadConsent: { event_sponsor_id: action.event_sponsor_id, interested: action.interested },
            };
        case 'RESET_WIDGET_PURCHASE':
            return {
                ...state,
                step: 'tickets',
                cart: [],
                selectedSeats: [],
                selectedTables: [],
                holdId: null,
                holdExpiresAt: null,
                holdApiSource: null,
                tableReservationHoldCents: null,
                purchaseResult: null,
                feeCalculation: null,
                calculatingFees: false,
                discount: null,
                unlockedSponsorTiers: {},
                sponsorVideoTierId: null,
                userReferralCode: '',
                viewCorrelationId: undefined,
                watchedSponsorAttributions: [],
                sponsorLeadQueue: [],
                sponsorLeadConsent: undefined,
                // Keep widgetBookingPath so "Get more tickets" / shop again matches prior flow
            };
        case 'SET_WIDGET_BOOKING_PATH': {
            if (action.path === state.widgetBookingPath) return state;
            if (action.path === 'tables') {
                return {
                    ...state,
                    widgetBookingPath: 'tables',
                    cart: [],
                    discount: null,
                    feeCalculation: null,
                    calculatingFees: false,
                    unlockedSponsorTiers: {},
                    sponsorVideoTierId: null,
                };
            }
            return {
                ...state,
                widgetBookingPath: 'tickets',
                selectedSeats: [],
                selectedTables: [],
                holdId: null,
                holdExpiresAt: null,
                holdApiSource: null,
                tableReservationHoldCents: null,
            };
        }
        default:
            return state;
    }
}

type Dispatch = (action: WidgetAction) => void;

export const StoreContext = createContext<{
    state: WidgetState;
    dispatch: Dispatch;
}>({ state: initialState, dispatch: () => {} });

export function useStore() {
    return useContext(StoreContext);
}

export function useWidgetStore(
    widgetId: string,
    prefillContact?: { name?: string; email?: string; phone?: string },
    refCode?: string,
    supportRegistration?: boolean,
) {
    const [state, dispatch] = useReducer(widgetReducer, {
        ...initialState,
        widgetId,
        refCode: refCode || '',
        supportRegistrationOverride: supportRegistration ?? true,
        contact: {
            name: prefillContact?.name || '',
            email: prefillContact?.email || '',
            phone: prefillContact?.phone || '',
        },
    });
    return { state, dispatch };
}
